import * as _ from 'underscore';
import { StudioApplication } from '..';
import { ID, Icons } from '../../const';
import { Templates } from '../../compile';
import { Size } from 'mappMvc/const';
import { View } from 'mappMvc/models/views';
import { Notification, Util } from 'mappMvc/models';
import { NotificationType, KeyCode } from 'mappMvc/enums';
import { BaseForm } from 'mappMvc/models/ui/forms';
import {
	Checkbox,
	Label,
	Input,
	NotificationElement,
	RequiredInput
} from 'mappMvc/models/ui/uiElements';
import { ViewOptions } from 'mappMvc/interfaces/options';
import { UserActivationClient } from 'mappClient/clients';
import { KoExtensions } from 'mappMvc/extensions/ko';
import { Localization } from 'mappMvc/compile';

export class Activation extends View {
	private activation_token: string;
	private name: KnockoutObservable<string>;
	private fullName: KnockoutObservable<string>;
	private notification: Notification;
	private password: KnockoutObservable<string>;
	private password_confirm: KnockoutObservable<string>;
	private form: BaseForm;
	private editable: KnockoutObservable<boolean>;
	private hidePassword: KnockoutObservable<boolean>;

	constructor(option: ViewOptions) {
		super(
			_.defaults(option, <ViewOptions>{
				viewId: ID.Activation,
				template: Templates.base_centered
			})
		);
		this.activation_token = null;

		this.name = ko.observable<string>();
		this.fullName = KoExtensions.observable<string>();
		this.password = ko.observable<string>();
		this.password_confirm = ko.observable<string>();
		this.editable = ko.observable<boolean>(true);
		this.hidePassword = ko.observable<boolean>(true);
		this.notification = new Notification({ text: Localization.Activation.DefinePassword });

		this.form = new BaseForm({
			initDefaultCommands: false,
			commands: [
				{
					command: () => this.form.submit(),
					label: Localization.Activation.Activate,
					css: Icons.Ok,
					template: Templates.forms_action
				}
			]
		});

		this.form.addUIElement(new NotificationElement(null, null, this.notification));

		this.form.addUIElement(
			new Label(ID.Fullname, Localization.Login.Fullname, this.fullName, {
				visible: ko.pureComputed(() => !!this.fullName())
			})
		);

		this.form.addUIElement(
			new Label(ID.Username, Localization.Login.Username, this.name, {
				size: Size._2_3
			})
		);

		this.form.addUIElement(
			new Checkbox(ID.HidePassword, Localization.Login.HidePassword, this.hidePassword, {
				size: Size._1_3,
				label: {
					size: Size._1_1
				},
				template: Templates.layouts_uiElements_noValidation,
				editable: this.editable
			})
		);

		var passwordUIElement = new RequiredInput<string>(
			ID.Password,
			Localization.Login.Password,
			this.password,
			{
				template: Templates.layouts_uiElements_labelValidationWidget,
				widget: {
					widget: Templates.widgets_password
				},
				editable: this.editable
			}
		);
		this.form.addUIElement(passwordUIElement);
		let confirmPasswordEditable = ko.pureComputed(() => this.editable() && this.hidePassword());
		this.form.addUIElement(
			new Input<string>(
				ID.PasswordConfirm,
				Localization.Login.ConfirmPassword,
				this.password_confirm,
				{
					label: {
						size: Size._1_2
					},
					validation: {
						size: Size._1_2
					},
					template: Templates.layouts_uiElements_labelValidationWidget,
					widget: {
						widget: Templates.widgets_password
					},
					editable: confirmPasswordEditable,
					rules: [
						{
							rule: 'required',
							params: confirmPasswordEditable
						},
						{
							rule: 'equal',
							params: this.password,
							condition: () => confirmPasswordEditable()
						}
					]
				}
			)
		);

		this.hidePassword.subscribe(newValue => {
			if (newValue) {
				passwordUIElement.setWidget(Templates.widgets_password);
			} else {
				passwordUIElement.setWidget(Templates.widgets_input);
				this.password_confirm(null);
			}
		});

		this.form.ClickedSubmit.subscribe(() => {
			if (this.editable()) {
				this.activateUser();
			}
		});

		this.ViewActivated.subscribe(() => {
			Util.bindKey(KeyCode.ENTER, this.form.submit, this.form);
		});

		this.ViewDeactivated.subscribe(() => {
			Util.unbindKey(KeyCode.ENTER, this.form.submit);
		});
	}

	private activateUser(): void {
		this.editable(false);
		this.notification.notify(Localization.Progress.PleaseWait, NotificationType.Hint);

		UserActivationClient.isPasswordValid({ value: this.password() }).then(validationResult => {
			if (!validationResult.isValid) {
				this.notification.notify(validationResult.message, NotificationType.Error);
				this.editable(true);
				this.password.setError(Localization.Errors.PasswordMismatch);
			} else {
				UserActivationClient.activateUser(this.activation_token, {
					password: this.password()
				}).then(
					() => {
						(<StudioApplication>this.parent).forceLogIn();
					},
					() => {
						this.notification.notify(Localization.Errors.Activation, NotificationType.Error);
						this.editable(true);
					}
				);
			}
		});
	}

	//overwrite
	public activated(routeSections: string[]): void {
		super.activated(routeSections);
		Util.setValue(null, this.password, this.password_confirm);
		this.form.hideValidationMessages();
		if (routeSections[0] === ID.Activation && !_.isEmpty(routeSections[1])) {
			this.activation_token = routeSections[1];
			this.getUserInfo();
		}
	}

	protected getUserInfo() {
		UserActivationClient.getUserInfo(this.activation_token).then(
			userActivation => {
				this.name(userActivation.name);
				this.fullName(userActivation.fullName);
			},
			error => {
				this.notification.notify(Localization.Errors.Activation, NotificationType.Error);
			}
		);
	}
}
